import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, ListItemIcon } from '@mui/material';
// routes
import { PATH_DASHBOARD, PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useResponsive from 'src/hooks/useResponsive';
import useMerchant from 'src/hooks/useMerchant';
import useCarbonUser from 'src/hooks/useCarbonUser';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import mixpanel from 'mixpanel-browser';
import { AccountBadge } from '../../../components/subscription/AccountBadge';
import Iconify from 'src/components/Iconify';
import MerchantSwitcher from './MerchantSwitcher';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Account',
    linkTo: PATH_DASHBOARD.account.accountDetails,
    icon: <AccountBadge />
  },
  {
    label: 'Subscription',
    linkTo: PATH_DASHBOARD.account.plans,
  },
  {
    label: 'User management',
    linkTo: PATH_DASHBOARD.account.userManagement
  }
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const mdUp = useResponsive('up', 'md');

  const { getMerchantUsers, merchantUsers } = useMerchant();

  const { user, logout } = useAuth();
  const { carbonUser } = useCarbonUser();
  const { merchant } = useMerchant();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    if(carbonUser && (!merchantUsers || merchantUsers.length === 0)) {
      getMerchantUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantUsers, carbonUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      //removes currently set data; in case a different user logs in next
      mixpanel.reset();
      if (isMountedRef.current) {
        handleClose();
      }
      window.location.reload();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const onChangeMerchant = (merchantId: string) => {
    localStorage.setItem('storedMerchantId', JSON.stringify(merchantId));
    navigate(PATH_DASHBOARD.root, { replace: true });
    window.location.reload();
    handleClose();
  };

  return (
    <>
      <Stack spacing={2} sx={{ ml: '8px !important' }} direction={'row'} alignItems={'center'} onClick={handleOpen}>
        {mdUp && 
          <Iconify
            icon={'solar:alt-arrow-down-linear'}
            color="text.primary" width={18} height={18}
          />
        }
        <IconButtonAnimate
          sx={{
            p: 0,
            ...(open && {
              '&:before': {
                zIndex: 1,
                content: "''",
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                position: 'absolute',
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
              },
            }),
          }}
        >

          <MyAvatar />
        </IconButtonAnimate>
      </Stack>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          border: (theme) => `1px solid ${theme.palette.grey[300]}`,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
            minHeight: 36,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant='h6' noWrap>
            {user?.name}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <MerchantSwitcher onChange={onChangeMerchant} />

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ py: 1 }}>
          {merchant && merchant.name && <Typography variant="h6" sx={{ p: '0.6rem 1rem' }}>
            {merchant.name}
          </Typography>}
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
            >
              <Typography>{option.label}</Typography>
              {option?.icon &&
                <ListItemIcon sx={{ ml: 1 }}>
                  {option.icon}
                </ListItemIcon>
                }
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
          <Typography>Logout</Typography>
        </MenuItem>
      </MenuPopover>
    </>
  );
}
